import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from "@angular/core";
import { LocationStrategy, PathLocationStrategy } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";

import { AppComponent } from "./app.component";
import { HttpClientModule } from "@angular/common/http";
import { ToasterModule, ToasterService } from "angular2-toaster";

import { FacebookService } from "ngx-facebook";

// Import containers
import { FullLayoutComponent, SimpleLayoutComponent } from "./containers";

const APP_CONTAINERS = [FullLayoutComponent, SimpleLayoutComponent];

// Import components
import {
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppFooterComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
  AppSidebarMinimizerComponent,
  APP_SIDEBAR_NAV,
} from "./components";

import {
  AnalyticsService,
  CandidatesService,
  CommonService,
  SessionService,
  TokenInterceptor,
} from "./services";

import { FormlyModule } from "@ngx-formly/core";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";

const APP_COMPONENTS = [
  AppAsideComponent,
  AppBreadcrumbsComponent,
  AppFooterComponent,
  AppHeaderComponent,
  AppSidebarComponent,
  AppSidebarFooterComponent,
  AppSidebarFormComponent,
  AppSidebarHeaderComponent,
  AppSidebarMinimizerComponent,
  APP_SIDEBAR_NAV,
];

// Import directives
import {
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  ReplaceDirective,
  SIDEBAR_TOGGLE_DIRECTIVES,
} from "./directives";

const APP_DIRECTIVES = [
  AsideToggleDirective,
  NAV_DROPDOWN_DIRECTIVES,
  ReplaceDirective,
  SIDEBAR_TOGGLE_DIRECTIVES,
];

import { AuthGuard, AuthNavGuard } from "./auth";

// Import routing module
import { AppRoutingModule } from "./app.routing";

// Import 3rd party components
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { TabsModule } from "ngx-bootstrap/tabs";
import { ModalModule } from "ngx-bootstrap/modal";
import { CollapseModule } from "ngx-bootstrap/collapse";
import { ChartsModule } from "ng2-charts/ng2-charts";
import { HTTP_INTERCEPTORS } from "@angular/common/http";
import { LocalStorageHelpers, SystemHelpers } from "./helpers";
import { SharedModule } from "./shared/shared.module";
import { ClevertapEventsService } from "./services/clevertap.service";

import { RouteReuseStrategy } from "@angular/router";
import { CustomReuseStrategy } from "./app.reuse.strategy";
import { SocketService } from "./services/socket.service";
import { ErrorHandlerHelper } from "./helpers/errorHandlerHelper";
import { VersionChecker } from "./helpers";
import { CustomRouteReuseStrategy } from "./route-reuse.strategy";
import { AppService } from "./services/app.service";
import { DpDatePickerModule } from "ng2-date-picker";
import { FeedbackModule } from "./feedback/feedback.module";
import { FormlyFieldSwitch } from "./formly/components/switch";
import { ColWrapperComponent } from "./formly/components/col-wrapper";
import { FormlyFieldDate } from "./formly/components/date";
import { FormlyFieldEditor } from "./formly/components/editor";
import { PanelWrapperComponent } from "./formly/components/panel-wrapper.component";
import { RowWrapperComponent } from "./formly/components/row-wrapper";
import { FormlyFieldSubmit } from "./formly/components/submit";
import { FormlyFieldTypeahead } from "./formly/components/typeahead";
import { CommonComponentModule } from "./common-components/common-component.module";
import { MatSliderModule } from "@angular/material";
import { CorsInterceptor } from "./services/corsInterceptor";
import { NoCacheInterceptor } from "./services/noCacheInterceptor";

@NgModule({
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    AppRoutingModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    TooltipModule.forRoot(),
    BsDropdownModule.forRoot(),
    TabsModule.forRoot(),
    ModalModule.forRoot(),
    CollapseModule.forRoot(),
    ToasterModule.forRoot(),
    ChartsModule,
    HttpClientModule,
    SharedModule,
    FeedbackModule,
    ReactiveFormsModule,
    CommonComponentModule,
    MatSliderModule,
    // FormlyModule.forRoot({
    //   types: [
    //     {
    //       name: "date",
    //       component: FormlyFieldDate,
    //       wrappers: ["form-field"],
    //     },
    //     {
    //       name: "switch",
    //       component: FormlyFieldSwitch,
    //       wrappers: ["form-field"],
    //     },
    //     {
    //       name: "editor",
    //       component: FormlyFieldEditor,
    //       wrappers: ["form-field"],
    //     },
    //     {
    //       name: "submit",
    //       component: FormlyFieldSubmit,
    //       wrappers: ["form-field"],
    //     },
    //     {
    //       name: "typeahead",
    //       component: FormlyFieldTypeahead,
    //       wrappers: ["form-field"],
    //     },
    //   ],
    //   validationMessages: [
    //     // { name: 'Position', message: (err, field: FormlyFieldConfig) => `"${field.formControl.value}" is not a valid IP Address`},
    //     { name: "required", message: "This field is required" },
    //     { name: "Editor", message: "This field is required" },
    //   ],
    //   wrappers: [
    //     { name: "panel", component: PanelWrapperComponent },
    //     { name: "row", component: RowWrapperComponent },
    //     { name: "col", component: ColWrapperComponent },
    //   ],
    // }),
    // FormlyBootstrapModule,
  ],
  declarations: [
    AppComponent,
    ...APP_CONTAINERS,
    ...APP_COMPONENTS,
    ...APP_DIRECTIVES,
    // FormlyFieldSwitch
    // FormlyFieldSwitch,
    // FormlyFieldEditor,
    // FormlyFieldTypeahead,
    // FormlyFieldSubmit,
    // PanelWrapperComponent,
    // RowWrapperComponent,
    // ColWrapperComponent,
    // FormlyFieldDate,
  ],
  providers: [
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    { provide: HTTP_INTERCEPTORS, useClass: NoCacheInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: CorsInterceptor, multi: true },
    {
      provide: RouteReuseStrategy,
      useClass: CustomReuseStrategy,
    },
    // new custom route reuse strategy
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerHelper,
    },
    HttpClientModule,
    CommonService,
    AuthGuard,
    AuthNavGuard,
    ToasterService,
    AnalyticsService,
    FacebookService,
    SystemHelpers,
    LocalStorageHelpers,
    ClevertapEventsService,
    SessionService,
    // SocketService,
    VersionChecker,
    AppService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
